import type { USP } from "../../../../../aysa_api/models/usp.types";
import type {
  Delivery,
  UnavailableProductTile,
} from "../../../../../aysa_api/services/basket/v1/delivery.types";
import type { TotalPrice } from "../../../../../aysa_api/services/basket/v1/total_price.types";
import type { PaymentDeliveryImage } from "../../../../../aysa_api/models/payment_delivery_image.types";
import type { ShippingCosts } from "../../../../../aysa_api/services/basket/v1/shipping_costs.types";
import type { Money } from "../../../../../aysa_api/models/money.types";
import type { Color } from "../../../../../aysa_api/models/color.types";
import type { Image } from "../../../../../aysa_api/models/image.types";
import type { LinkTarget } from "../../../../../aysa_api/models/link_target.types";
import type { Advertisement } from "../../../../../aysa_api/models/advertisement.types";
import type { ContactInfo } from "../../../../../aysa_api/models/contact_info.types";

export enum InfoSection_Info_PlacementOption {
  UNSPECIFIED = 0,
  ABOVE_CHECKOUT_SECTION = 1,
  BELOW_CHECKOUT_SECTION = 2,
}

export interface USPsSection {
  usps: USP[];
  type?: {
    $case: "usp";
    usp: USPsSection_USP;
  };
}

export interface USPsSection_USP {
  usps: USP[];
}

// A section containing all deliveries for the current basket
export interface DeliverySection {
  deliveries: Delivery[];
  type?:
    | {
        $case: "delivery";
        delivery: DeliverySection_Delivery;
      }
    | {
        $case: "empty";
        // the empty state here applies to the whole empty basket UI
        // and doesn't necessarily only mean that there are just no deliveries
        empty: DeliverySection_Empty;
      };
}

export interface DeliverySection_Delivery {
  deliveries: Delivery[];
}

export interface DeliverySection_Empty {}

export interface UnavailableProductsSection {
  items: UnavailableProductTile[];
  type?: {
    $case: "tiles";
    tiles: UnavailableProductsSection_UnavailableProductTile;
  };
}

export interface UnavailableProductsSection_UnavailableProductTile {
  items: UnavailableProductTile[];
}

// Section containing total price, discounts, redirect link to checkout, payment method icons, shipping costs
export interface TotalPriceSection {
  totalPrice?: TotalPrice;
  paymentMethods: PaymentDeliveryImage[];
  // Needed on the new basketUI to conditionally render and load App install teaser(s)
  // as an intermediary step before user going to CO.
  showAppInstallTeaser: boolean;
  shippingCosts: ShippingCosts;
  basketPriceSummary: TotalPriceSection_PriceBreakdown;
  type?: {
    $case: "totalPriceInfo";
    totalPriceInfo: TotalPriceSection_TotalPriceInfo;
  };
}

export interface TotalPriceSection_TotalPriceInfo {
  // Use [PriceBreakdown] for the checkout section
  totalPrice: TotalPrice;
  paymentMethods: PaymentDeliveryImage[];
  // Needed on the new basketUI to conditionally render and load App install teaser(s)
  // as an intermediary step before user going to CO.
  showAppInstallTeaser: boolean;
  expressCheckoutProviders: TotalPriceSection_TotalPriceInfo_ExpressCheckoutProvider[];
  disableGoToCheckout: boolean;
}

export interface TotalPriceSection_TotalPriceInfo_ExpressCheckoutProvider {
  type?: {
    $case: "paypal";
    paypal: TotalPriceSection_TotalPriceInfo_ExpressCheckoutProvider_PayPal;
  };
}

export interface TotalPriceSection_TotalPriceInfo_ExpressCheckoutProvider_PayPal {
  basketId: string;
}

// eg:
// [rows]
// [sticky_rows]
// [checkout_total]
export interface TotalPriceSection_PriceBreakdown {
  // Rows that come before sticky_rows with additional information
  // Don't need to be always visible
  // They don't include [sticky_rows]
  // eg 'Full price', 'Sale price'
  rows: TotalPriceSection_PriceBreakdown_Row[];
  // Rows that are always visible - sticky
  // eg 'Shipping'
  stickyRows: TotalPriceSection_PriceBreakdown_Row[];
  checkoutTotal: TotalPriceSection_PriceBreakdown_CheckoutTotal;
  coinsRow?: TotalPriceSection_PriceBreakdown_CoinsRow;
}

export interface TotalPriceSection_PriceBreakdown_Row {
  // eg 'Full Price'
  label: string;
  // eg also negative numbers that represent: '-30,00 €'
  amount: Money;
  secondaryPriceLabel?: string;
  isStrikeThrough: boolean;
  foregroundColor: Color;
  backgroundColor?: Color;
  // / Always visible when [is_sticky] = true
  // / NOTE: On the App we merge [...[PriceBreakdown.rows], ...[PriceBreakdown.sticky_rows]] to enable interleaving of sticky & non-sticky rows
  isSticky: boolean;
}

// Ultimate price the customer needs to pay
// Visible together with [sticky_rows]
export interface TotalPriceSection_PriceBreakdown_CheckoutTotal {
  label: string;
  // eg 'incl. VAT'
  taxInformation?: string;
  amount: Money;
  secondaryPriceLabel?: string;
}

export interface TotalPriceSection_PriceBreakdown_CoinsRow {
  // e.g "You will earn"
  title: string;
  // e.g "+ 1.500 Coins"
  value: string;
}

// Section containing icons of AY loyalty partners, shipping and partners
export interface PartnersSection {
  partners: PartnersSection_Partner[];
  type?: {
    $case: "partner";
    partner: PartnersSection_PartnerList;
  };
}

export interface PartnersSection_PartnerList {
  items: PartnersSection_Partner[];
  // This title should be dynamic based on the locale or an ongoing AB Test
  // For now it should be available only on DE as this is the only country
  // we have the parners section
  //
  // Later this title is expected to return either
  // - 'Accepted payment methods'
  // or
  // - 'Accepted payment methods & partners'
  headline: string;
}

export interface PartnersSection_Partner {
  image: Image;
  label: string;
  link?: LinkTarget;
}

export interface AdvertisementBannerSection {
  advertisement?: Advertisement;
  type?: {
    $case: "ads";
    ads: AdvertisementBannerSection_Advertisement;
  };
}

export interface AdvertisementBannerSection_Advertisement {
  advertisement: Advertisement;
}

export interface ResaleBagSection {
  type?:
    | {
        $case: "notEligible";
        notEligible: ResaleBagSection_NotEligible;
      }
    | {
        $case: "eligibleOptedIn";
        eligibleOptedIn: ResaleBagSection_EligibleOptedIn;
      }
    | {
        $case: "eligibleNotOptedIn";
        eligibleNotOptedIn: ResaleBagSection_EligibleNotOptedIn;
      };
}

export interface ResaleBagSection_NotEligible {}

export interface ResaleBagSection_EligibleOptedIn {}

export interface ResaleBagSection_EligibleNotOptedIn {}

export interface TitleSection {
  basketItemCount: number;
  totalPrice?: TotalPrice;
  type?: {
    $case: "title";
    title: TitleSection_TitleInfo;
  };
}

export interface TitleSection_TitleInfo {
  basketItemCount: number;
  totalPrice: TotalPrice;
}

export interface FAQSection {
  articles: FAQSection_FAQArticle[];
  type?: {
    $case: "articleList";
    articleList: FAQSection_ArticleList;
  };
}

export interface FAQSection_ArticleList {
  article: FAQSection_FAQArticle[];
}

export interface FAQSection_FAQArticle {
  headline: string;
  // Formated html with paragraphs
  text: string;
}

export interface CustomerServiceSection {
  phone?: ContactInfo;
  email?: ContactInfo;
  whatsapp?: ContactInfo;
  type?: {
    $case: "contactInfo";
    contactInfo: CustomerServiceSection_ContactInfo;
  };
}

export interface CustomerServiceSection_ContactInfo {
  phone?: ContactInfo;
  email?: ContactInfo;
  whatsapp?: ContactInfo;
}

export interface InfoSection {
  sections: InfoSection_Info[];
  type?: {
    $case: "informationPack";
    informationPack: InfoSection_InformationPack;
  };
}

export interface InfoSection_Info {
  // main title of the information pack
  // strictly its almost a string, but in reality it can be a string or a stringified html
  title: string;
  // the additional content readable after the user clicks on the dropdown toggle. If empty, no dropdown wil be shown on the UI.
  expandableContent?: string;
  // which icon to use. If empty, no icon will be shown on the UI.
  icon?: Image;
  // FE will need information about the placement, so we can filter information sections
  // to either show the tag above or below the information section.
  placement: InfoSection_Info_PlacementOption;
  // this applies to : title, icon (default is system default)
  foregroundColor?: string;
}

export interface InfoSection_InformationPack {
  sections: InfoSection_Info[];
}

export interface HintSection {
  type?:
    | {
        $case: "warning";
        warning: HintSection_Warning;
      }
    | {
        $case: "error";
        error: HintSection_Error;
      };
}

export interface HintSection_Warning {
  headline: string;
  subline?: string;
}

export interface HintSection_Error {
  message: string;
}

export interface CoinsSection {
  // e.g Redeem Coins
  title: string;
  // e.g {available coins} Coins ({equivalent amount}) available
  subtitle: string;
  // e.g "NEW"
  badge?: string;
  sticky?: CoinsSection_Sticky;
  type?:
    | {
        $case: "unavailable";
        unavailable: CoinsSection_Unavailable;
      }
    | {
        $case: "error";
        error: CoinsSection_Error;
      }
    | {
        $case: "available";
        available: CoinsSection_Available;
      };
}

// This indicates that the user is unable to toggle it on or off
export interface CoinsSection_Unavailable {
  // e.g "To apply discounts with coins, your order value must be at least {amount}"
  notification: string;
}

// This indicates that the user is unable to toggle it on or off
export interface CoinsSection_Error {
  // e.g "Discount removed. To apply discounts with coins, your order value must be at least {amount}"
  notification: string;
}

// This indicates that the user is able to toggle it on or off
export interface CoinsSection_Available {
  // Current state of the toggle
  applied: boolean;
  // e.g 1.500
  coins: string;
  // e.g 15,00 €
  equivalentAmount: string;
}

export interface CoinsSection_Sticky {
  // e.g "Use coins"
  title: string;
  // e.g "New"
  badge?: string;
}
